import React from "react";
import {
  Eye, ScanEye, Sliders, Gauge, Droplets, CloudDrizzle, Wind, Snowflake,
  Thermometer, Volume2, VolumeX, Zap, ZapOff, Leaf, ShieldCheck, Sparkles,
  Timer, Wifi, Moon, Sun, Filter, Activity, Bot, Cpu, MicOff, Flame,
  ThermometerSun, ThermometerSnowflake, Fan, Radio, Smartphone, Battery,
  BatteryCharging, Sprout, Plug, Settings, Clock, Sunrise, Bluetooth,
  PackageOpen, Package, CircleDot, Gem, Award, Star, Recycle, Bug, BugOff,
  Hand, HandMetal, Fingerprint, Layers, LineChart, Lock, Router, ShieldAlert,
  Shield, Smile, Speaker, Tv, Umbrella, Users, Waves, Wrench, Aperture,
  Grid3x3, Rocket, SlidersHorizontal, Settings2, Droplet, UserCheck,
} from "lucide-react";

/**
 * Stable string-key → Lucide icon map. Extend this object to add new icons
 * (admin picker reads the full ICON_LIST below). Icon keys are lower-case
 * kebab-case so they survive JSON serialization and URL params cleanly.
 *
 * When adding:
 *   1. add the entry to ICONS (key → component)
 *   2. add the matching metadata row to ICON_LIST (key + EN + AR defaults)
 */
export const ICONS = {
  // --- Intelligence / automation ---
  "smart-eye": ScanEye,
  "intelligent-eye": Eye,
  "ai": Sparkles,
  "ai-pro": Bot,
  "cpu": Cpu,
  "smart-app": Smartphone,
  "wifi": Wifi,
  "bluetooth": Bluetooth,
  "voice": Radio,
  "voice-off": MicOff,

  // --- Modes ---
  "gear-shift": Sliders,
  "modes": Gauge,
  "sleep": Moon,
  "day": Sun,
  "timer": Timer,
  "clock": Clock,
  "sunrise": Sunrise,
  "settings": Settings,
  "layers": Layers,

  // --- Climate / air ---
  "humidity": Droplets,
  "humidity-control": CloudDrizzle,
  "wind": Wind,
  "fan": Fan,
  "cool": Snowflake,
  "heat": Flame,
  "cool-heat": ThermometerSnowflake,
  "warm": ThermometerSun,
  "thermo": Thermometer,
  "waves": Waves,

  // --- Energy / eco ---
  "energy": Zap,
  "energy-save": ZapOff,
  "eco": Leaf,
  "eco-plus": Sprout,
  "recycle": Recycle,
  "green-plug": Plug,
  "battery": Battery,
  "battery-charge": BatteryCharging,

  // --- Sound ---
  "quiet": VolumeX,
  "sound": Volume2,
  "speaker": Speaker,

  // --- Protection / durability ---
  "warranty": ShieldCheck,
  "shield": Shield,
  "shield-alert": ShieldAlert,
  "lock": Lock,
  "filter": Filter,
  "anti-bug": BugOff,
  "bug": Bug,
  "fingerprint": Fingerprint,

  // --- Premium / awards ---
  "gem": Gem,
  "award": Award,
  "star": Star,
  "aperture": Aperture,
  "carbon-fiber": Grid3x3,        // Carbon fiber look
  "mirror": Aperture,             // Mirror finish (reflective/aperture-like)

  // --- New boost / cleaning / protection ---
  "turbo": Rocket,                // Turbo Mood
  "gear-shift-v2": SlidersHorizontal,   // Gear Shift 2.0
  "gear-shift-v3": Settings2,           // Gear Shift 3.0
  "active-clean": Droplet,              // Active Clean (self-cleaning)
  "smart-guard": UserCheck,             // Smart Guard (adaptive protection)

  // --- Misc useful ---
  "activity": Activity,
  "chart": LineChart,
  "circle": CircleDot,
  "hand": Hand,
  "hand-metal": HandMetal,
  "smile": Smile,
  "tv": Tv,
  "umbrella": Umbrella,
  "users": Users,
  "wrench": Wrench,
  "router": Router,
  "package": Package,
  "package-open": PackageOpen,
};

/**
 * Admin picker metadata — each row is { key, en, ar }.
 * Grouped by domain for the picker UI. Admin can override en/ar labels.
 */
export const ICON_LIST = [
  // Intelligence
  { key: "smart-eye",        en: "Intelligent Eye",  ar: "العين الذكية" },
  { key: "ai",               en: "AI Mode",          ar: "الوضع الذكي" },
  { key: "ai-pro",           en: "AI Pro",           ar: "AI برو" },
  { key: "cpu",              en: "Smart Chip",       ar: "شريحة ذكية" },
  { key: "smart-app",        en: "Smart App",        ar: "تطبيق ذكي" },
  { key: "wifi",             en: "Wi-Fi Control",    ar: "تحكم عن بُعد" },
  { key: "bluetooth",        en: "Bluetooth",        ar: "بلوتوث" },
  { key: "voice",            en: "Voice Control",    ar: "التحكم الصوتي" },

  // Modes
  { key: "gear-shift",       en: "Gear Shift",       ar: "جير شفت" },
  { key: "modes",            en: "Multi Modes",      ar: "أوضاع متعددة" },
  { key: "sleep",            en: "Sleep Mode",       ar: "وضع النوم" },
  { key: "day",              en: "Day Mode",         ar: "وضع النهار" },
  { key: "timer",            en: "24h Timer",        ar: "مؤقت ٢٤ ساعة" },
  { key: "clock",            en: "Scheduler",        ar: "جدولة" },
  { key: "sunrise",          en: "Wake-Up Mode",     ar: "وضع الاستيقاظ" },

  // Climate
  { key: "humidity",         en: "Humidity Control", ar: "التحكم بالرطوبة" },
  { key: "wind",             en: "Wind Flow",        ar: "تدفق الهواء" },
  { key: "fan",              en: "Fan Speed",        ar: "سرعة المروحة" },
  { key: "cool",             en: "Powerful Cooling", ar: "تبريد قوي" },
  { key: "heat",             en: "Heating",          ar: "تدفئة" },
  { key: "cool-heat",        en: "Cool & Heat",      ar: "تبريد وتدفئة" },
  { key: "warm",             en: "Warm Mode",        ar: "الوضع الدافئ" },
  { key: "thermo",           en: "Precise Temp",     ar: "درجة حرارة دقيقة" },

  // Energy
  { key: "energy",           en: "Energy Efficient", ar: "موفّر للطاقة" },
  { key: "energy-save",      en: "Low Power",        ar: "استهلاك منخفض" },
  { key: "eco",              en: "Eco Mode",         ar: "وضع Eco" },
  { key: "eco-plus",         en: "Eco+",             ar: "Eco+" },
  { key: "recycle",          en: "Recyclable",       ar: "قابل للتدوير" },
  { key: "battery-charge",   en: "Fast Recovery",    ar: "تشغيل سريع" },

  // Sound
  { key: "quiet",            en: "Quiet Operation",  ar: "تشغيل صامت" },

  // Protection
  { key: "warranty",         en: "Extended Warranty", ar: "كفالة ممتدة" },
  { key: "shield",           en: "Durable Build",    ar: "هيكل متين" },
  { key: "filter",           en: "Advanced Filter",  ar: "فلتر متقدم" },
  { key: "anti-bug",         en: "Anti-Insect",      ar: "مضاد للحشرات" },
  { key: "lock",             en: "Child Lock",       ar: "قفل الأطفال" },

  // Premium
  { key: "gem",              en: "Premium Finish",   ar: "تشطيب فاخر" },
  { key: "award",            en: "Award Winning",    ar: "حائز على جوائز" },
  { key: "star",             en: "Top Rated",        ar: "تقييم ممتاز" },
  { key: "carbon-fiber",     en: "Carbon Fiber Look", ar: "مظهر ألياف الكربون" },
  { key: "mirror",           en: "Mirror Finish",    ar: "تشطيب مرايا" },

  // Performance / cleaning / protection (new)
  { key: "turbo",            en: "Turbo Mood",       ar: "وضع التيربو" },
  { key: "gear-shift-v2",    en: "Gear Shift 2.0",   ar: "جير شفت 2.0" },
  { key: "gear-shift-v3",    en: "Gear Shift 3.0",   ar: "جير شفت 3.0" },
  { key: "active-clean",     en: "Active Clean",     ar: "تنظيف نشط" },
  { key: "smart-guard",      en: "Smart Guard",      ar: "الحارس الذكي" },
];

export default function ProductHighlights({ items = [], lang = "en", label = "Key features" }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="product-highlights" className="mt-10 border-t border-black/10 pt-8">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">
        {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((h, i) => {
          const Icon = ICONS[h.icon] || Sparkles;
          const text = lang === "ar" ? (h.label_ar || h.label) : h.label;
          return (
            <div
              key={`${h.icon}-${i}`}
              data-testid={`highlight-${h.icon}`}
              className="flex items-center gap-3 p-4 rounded-xl bg-secondary border border-black/5 hover:bg-white hover:border-electric/30 transition-colors"
            >
              <div className="shrink-0 w-11 h-11 rounded-lg bg-white grid place-items-center shadow-[0_4px_12px_-6px_rgba(0,0,0,0.15)]">
                <Icon size={22} className="text-electric" strokeWidth={1.75} />
              </div>
              <div className="text-sm font-display font-semibold tracking-tight leading-tight">
                {text}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
